import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Building2, Plus, Search, Mail, Phone, Eye, X, MapPin, Users, Archive, MoreHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { CreateClientSheet } from "./CreateClientSheet";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { LoadingSpinner } from "@/components/loading-spinner";
import { ProtectByPermission } from "@/components/auth";
import { EmptyState } from "@/components/ui/empty-state";
import { APIProvider } from "@vis.gl/react-google-maps";

// Account type options for filtering (matching the add/edit sheets)
const ACCOUNT_TYPES = [
  // Commercial Office Building
  { value: "corporate-office", label: "Corporate Office" },
  { value: "professional-service", label: "Professional Service Firm" },
  { value: "co-working", label: "Co-working Space" },
  { value: "business-park", label: "Business Park" },
  { value: "office-complex", label: "Office Complex" },
  // Educational Institution
  { value: "k-12", label: "K-12 School" },
  { value: "university", label: "University or College" },
  { value: "private-academy", label: "Private Academy" },
  { value: "vocational-school", label: "Vocational School" },
  { value: "daycare", label: "Daycare Center" },
  // Healthcare Facility
  { value: "hospital", label: "Hospital" },
  { value: "medical-clinic", label: "Medical Clinic" },
  { value: "dental-office", label: "Dental Office" },
  { value: "surgery-center", label: "Outpatient Surgery Center" },
  { value: "physical-therapy", label: "Physical Therapy Center" },
  { value: "long-term-care", label: "Long-term Care Facility" },
  // Retail Space
  { value: "shopping-mall", label: "Shopping Mall" },
  { value: "department-store", label: "Department Store" },
  { value: "boutique", label: "Boutique" },
  { value: "grocery-store", label: "Grocery Store" },
  { value: "big-box", label: "Big Box Retailer" },
  // Hospitality Industry
  { value: "hotel", label: "Hotel or Motel" },
  { value: "resort", label: "Resort" },
  { value: "convention-center", label: "Convention Center" },
  { value: "banquet-hall", label: "Banquet Hall" },
  { value: "restaurant", label: "Restaurant or Cafe" },
  // Financial Institution
  { value: "bank", label: "Bank" },
  { value: "credit-union", label: "Credit Union" },
  { value: "investment-firm", label: "Investment Firm" },
  { value: "insurance-company", label: "Insurance Company" },
  // Industrial Facility
  { value: "manufacturing", label: "Manufacturing Plant" },
  { value: "warehouse", label: "Warehouse" },
  { value: "distribution-center", label: "Distribution Center" },
  { value: "food-processing", label: "Food Processing Facility" },
  { value: "factory", label: "Factory" },
  // Religious Facility
  { value: "church", label: "Church" },
  { value: "synagogue", label: "Synagogue" },
  { value: "mosque", label: "Mosque" },
  { value: "religious-education", label: "Religious Education Center" },
  // Government Building
  { value: "municipal-office", label: "Municipal Office" },
  { value: "courthouse", label: "Courthouse" },
  { value: "post-office", label: "Post Office" },
  { value: "dmv", label: "DMV Location" },
  { value: "public-library", label: "Public Library" },
  // Entertainment Venue
  { value: "movie-theater", label: "Movie Theater" },
  { value: "concert-hall", label: "Concert Hall" },
  { value: "sports-arena", label: "Sports Arena" },
  { value: "museum", label: "Museum" },
  { value: "art-gallery", label: "Art Gallery" },
  // Transportation Hub
  { value: "airport", label: "Airport" },
  { value: "bus-terminal", label: "Bus Terminal" },
  { value: "train-station", label: "Train Station" },
  { value: "subway-station", label: "Subway Station" },
  // Residential Property
  { value: "apartment-complex", label: "Apartment Complex" },
  { value: "condominium", label: "Condominium" },
  { value: "homeowners-association", label: "Homeowners Association" },
];

interface Client {
  _id: string;
  name: string;
  accountType?: string;
  address?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  serviceStatus?: "active" | "inactive" | "pending";
}

export default function ClientList() {
  const navigate = useNavigate();
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  // Query to get clients from Convex
  const { user } = useUser();
  const getClients = useQuery(api.businesses.getClients, user ? {} : "skip") || [];
  const archiveClient = useMutation(api.businesses.archive);

  const handleArchive = async (clientId: Id<"businesses">, clientName: string) => {
    try {
      await archiveClient({ id: clientId });
      toast.success(`${clientName} has been archived successfully`);
    } catch (error) {
      console.error("Failed to archive client:", error);
      toast.error("Failed to archive client");
    }
  };

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== null || typeFilter !== null;

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter(null);
    setTypeFilter(null);
  };

  // Filter clients based on search query, status, and type
  const filteredClients = Array.isArray(getClients)
    ? getClients.filter((client) => {
        // Apply search filter
        const matchesSearch =
          searchQuery === "" ||
          client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (client.contactName?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
          (client.contactEmail?.toLowerCase() || "").includes(searchQuery.toLowerCase());

        // Apply status filter
        const matchesStatus = statusFilter === null || client.serviceStatus === statusFilter;

        // Apply type filter
        const matchesType = typeFilter === null || client.accountType === typeFilter;

        return matchesSearch && matchesStatus && matchesType;
      })
    : [];

  // Show loading state while fetching data
  if (getClients === undefined) {
    return (
      <DashboardLayout activeTab="clients">
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""}>
      <DashboardLayout activeTab="clients">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
              <p className="mt-1 text-gray-500">Manage your client relationships</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="gap-2" onClick={() => navigate('/clients/archived')}>
                <Archive className="h-4 w-4" /> Archived
              </Button>
              <ProtectByPermission permission="clients:write">
                <Button className="gap-2" onClick={() => setIsCreateSheetOpen(true)}>
                  <Plus className="h-4 w-4" /> Add Client
                </Button>
              </ProtectByPermission>
            </div>
          </div>

          <Card className="p-6">
            <div className="mb-6 flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Search clients..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select
                value={statusFilter || "all"}
                onValueChange={(value) => setStatusFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={typeFilter || "all"}
                onValueChange={(value) => setTypeFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {ACCOUNT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" /> Clear Filters
                </Button>
              )}
            </div>

            <div className="w-full overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Account Type</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <EmptyState
                          icon={Users}
                          title="No clients yet"
                          description="Add your first client to get started."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredClients.map((client) => (
                      <TableRow key={client._id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-gray-500" />
                              <span className="font-medium">{client.name}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{client.accountType || "N/A"}</span>
                        </TableCell>
                        <TableCell>{client.address || "N/A"}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div>{client.contactName || "N/A"}</div>
                            {(client.contactEmail || client.contactPhone) && (
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                {client.contactEmail && (
                                  <div className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" /> {client.contactEmail}
                                  </div>
                                )}
                                {client.contactPhone && (
                                  <div className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" /> {client.contactPhone}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                              client.serviceStatus === "active"
                                ? "bg-green-100 text-green-700"
                                : client.serviceStatus === "pending"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {(client.serviceStatus || "Active").charAt(0).toUpperCase() +
                              (client.serviceStatus || "Active").slice(1)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/clients/${client._id}`)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleArchive(client._id as Id<"businesses">, client.name)}
                                className="text-red-600"
                              >
                                <Archive className="mr-2 h-4 w-4" />
                                Archive
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
        <CreateClientSheet isOpen={isCreateSheetOpen} onClose={() => setIsCreateSheetOpen(false)} />
      </DashboardLayout>
    </APIProvider>
  );
}
